import { matchesFieldFilter } from '../shared/data-layer-fields.js';
import type { DataLayerDisplay } from '../shared/data-layer.js';
import { datasetIntersects as intersects } from './local-dataset-extent.js';
import { cleanupDatasetStore, discardDatasetChunks } from './local-dataset-cleanup.js';
import { z } from 'zod';
import { calculateDataLayerBounds, type DataFeature, type DataLayer, type DatasetReference } from '../shared/data-layer.js';
import { jsonByteLength } from '../shared/json-size.js';

const DATABASE = 'drawonamap-local-datasets';
const TTL = 7 * 24 * 60 * 60 * 1000;
const CHUNK_BYTES = 512 * 1024;
const boundsSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable();
const partSchema = z.object({ id: z.string(), count: z.number().int().nonnegative(), bounds: boundsSchema });
const manifestSchema = z.object({ id: z.string(), parts: z.array(partSchema), count: z.number(), columns: z.array(z.string()), bounds: boundsSchema, expiresAt: z.number() });
type Manifest = z.infer<typeof manifestSchema>;
type Bounds = NonNullable<Manifest['bounds']>;
interface Chunk { id: string; features: DataFeature[]; expiresAt: number }


async function database(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      for (const name of ['datasets', 'chunks', 'indexes']) request.result.createObjectStore(name, { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}
function read<T>(db: IDBDatabase, store: string, id: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = db.transaction(store).objectStore(store).get(id);
    request.onsuccess = () => request.result ? resolve(request.result) : reject(new Error('Local dataset records are missing.'));
    request.onerror = () => reject(request.error);
  });
}
function write(db: IDBDatabase, store: string, value: Manifest | Chunk | (ChunkIndex & { id: string })): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.oncomplete = () => resolve(); tx.onabort = () => reject(tx.error); tx.onerror = () => reject(tx.error);
    tx.objectStore(store).put(value);
  });
}
async function writeChunk(db: IDBDatabase, id: string, features: DataFeature[], expiresAt: number): Promise<void> {
  await write(db, 'chunks', { id, features, expiresAt });
  await write(db, 'indexes', { id, createdAt: Date.now(), ids: features.map(feature => feature.id), bounds: features.map(feature => calculateDataLayerBounds([feature])) });
}
interface ChunkIndex { createdAt?: number; ids: string[]; bounds: Array<Bounds | null> }
function boundsUnion(values: Array<Bounds | null>): Bounds | null {
  const bounds = values.filter(value => value !== null);
  return bounds.length ? bounds.reduce<Bounds>((a, b) => [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[2], b[2]), Math.max(a[3], b[3])], [...bounds[0]]) : null;
}
async function manifest(db: IDBDatabase, ref: DatasetReference): Promise<Manifest> {
  const value = manifestSchema.parse(await read(db, 'datasets', ref.id));
  if (value.expiresAt < Date.now()) throw new Error('The local dataset has expired. Import the file again.');
  return value;
}
function reference(value: Manifest): DatasetReference { return { id: value.id, count: value.count, columns: value.columns }; }

/** Publish the manifest last. A failed or cancelled import cannot replace a map. */
export async function datasetWriter() {
  const db = await database();
  const parts: Manifest['parts'] = [];
  const created: string[] = []; let published = false;
  const columns = new Set<string>();
  let batch: DataFeature[] = []; let bytes = 0;
  const expiresAt = Date.now() + TTL;
  async function flush() {
    if (!batch.length) return;
    const id = crypto.randomUUID();
    const bounds = calculateDataLayerBounds(batch);
    created.push(id);
    await writeChunk(db, id, batch, expiresAt);
    parts.push({ id, count: batch.length, bounds }); batch = []; bytes = 0;
  }
  return {
    async append(feature: DataFeature) {
      const size = jsonByteLength(feature);
      if (bytes + size > CHUNK_BYTES || batch.length >= 512) await flush();
      batch.push(feature); bytes += size;
      Object.keys(feature.properties).forEach(key => columns.add(key));
    },
    async finish(layer: DataLayer): Promise<DataLayer> {
      await flush();
      const value: Manifest = { id: crypto.randomUUID(), parts, count: parts.reduce((n, part) => n + part.count, 0), columns: [...columns], bounds: boundsUnion(parts.map(part => part.bounds)), expiresAt };
      await write(db, 'datasets', value); published = true;
      return { ...layer, features: [], featureCount: value.count, bounds: value.bounds, dataset: reference(value) };
    },
    async close() { try { if (!published && created.length) await discardDatasetChunks(db, created); } finally { db.close(); } },
  };
}

/** Iterate bounded chunks. Never accumulate a complete dataset in either thread. */
export async function* datasetBatches(ref: DatasetReference): AsyncGenerator<DataFeature[]> {
  const db = await database();
  try {
    const value = await manifest(db, ref);
    for (const part of value.parts) yield (await read<Chunk>(db, 'chunks', part.id)).features;
  } finally { db.close(); }
}

export async function datasetPage({ ref, offset, limit }: { ref: DatasetReference; offset: number; limit: number }) {
  const db = await database();
  try {
    const value = await manifest(db, ref); const features: DataFeature[] = [];
    let position = 0;
    for (const part of value.parts) {
      if (position + part.count > offset && position < offset + Math.min(limit, 100)) {
        const chunk = await read<Chunk>(db, 'chunks', part.id);
        features.push(...chunk.features.slice(Math.max(0, offset - position), Math.min(part.count, offset + Math.min(limit, 100) - position)));
      }
      position += part.count;
      if (position >= offset + Math.min(limit, 100)) break;
    }
    return features;
  } finally { db.close(); }
}

export async function datasetFeature({ ref, id }: { ref: DatasetReference; id: string }) {
  const db = await database();
  try {
    const value = await manifest(db, ref);
    for (const part of value.parts) {
      const index = await read<ChunkIndex>(db, 'indexes', part.id);
      const position = index.ids.indexOf(id);
      if (position >= 0) return (await read<Chunk>(db, 'chunks', part.id)).features[position];
    }
    return null;
  } finally { db.close(); }
}

/** Query a compact spatial index before reading only the selected record chunks. */
export async function datasetView({ ref, extent, display }: { ref: DatasetReference; extent: Bounds; display?: DataLayerDisplay }) {
  const db = await database();
  try {
    const value = await manifest(db, ref);
    const features: DataFeature[] = [];
    for (const part of value.parts) {
      if (!intersects(part.bounds, extent)) continue;
      const index = await read<ChunkIndex>(db, 'indexes', part.id);
      const positions = index.bounds.flatMap((bounds, position) => intersects(bounds, extent) ? [position] : []);
      if (!positions.length) continue;
      const chunk = await read<Chunk>(db, 'chunks', part.id);
      for (const position of positions) {
        const feature = chunk.features[position];
        if (matchesFieldFilter(feature, display)) features.push(feature);
      }
    }
    const matched = features.length;
    return { features, matched };
  } finally { db.close(); }
}

/** Copy only touched chunks. Old immutable manifests remain usable by undo. */
export async function datasetEdit({ ref, changes, deleted = [], color }: { ref: DatasetReference; changes: DataFeature[]; deleted?: string[]; color?: string }) {
  const db = await database();
  const created: string[] = [];
  try {
    const previous = await manifest(db, ref);
    const remaining = new Map(changes.map(feature => [feature.id, feature])); const removed = new Set(deleted);
    const parts: Manifest['parts'] = [];
    for (const part of previous.parts) {
      const index = await read<ChunkIndex>(db, 'indexes', part.id);
      if (!color && !index.ids.some(id => remaining.has(id) || removed.has(id))) { parts.push(part); continue; }
      const chunk = await read<Chunk>(db, 'chunks', part.id);
      let changed = false;
      const features = chunk.features.flatMap(feature => {
        if (removed.has(feature.id)) { changed = true; return []; }
        const next = remaining.get(feature.id);
        if (!next) { if (!color) return [feature]; changed = true; return [{ ...feature, sourceStyle: { ...feature.sourceStyle, color, fillColor: color } }]; }
        remaining.delete(feature.id); changed = true; return [next];
      });
      if (!changed) { parts.push(part); continue; }
      const id = crypto.randomUUID();
      created.push(id);
      await writeChunk(db, id, features, Date.now() + TTL);
      if (features.length) parts.push({ id, count: features.length, bounds: calculateDataLayerBounds(features) });
    }
    if (remaining.size) throw new Error('The edited feature is no longer in this dataset.');
    const next: Manifest = { ...previous, id: crypto.randomUUID(), parts, count: parts.reduce((n, part) => n + part.count, 0), bounds: boundsUnion(parts.map(part => part.bounds)), columns: [...new Set([...previous.columns, ...changes.flatMap(feature => Object.keys(feature.properties))])], expiresAt: Date.now() + TTL };
    await write(db, 'datasets', next);
    return { dataset: reference(next), bounds: next.bounds, featureCount: next.count };
  } catch (error) {
    if (created.length) await discardDatasetChunks(db, created);
    throw error;
  } finally { db.close(); }
}

export async function retainDataset(ref: DatasetReference): Promise<void> {
  const db = await database();
  try { const value = await manifest(db, ref); await write(db, 'datasets', { ...value, expiresAt: Date.now() + TTL }); }
  finally { db.close(); }
}

export async function cleanupLocalDatasets(): Promise<void> {
  const db = await database();
  try { await cleanupDatasetStore(db); } finally { db.close(); }
}
